import { Component, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { UrlBuilder } from '../core/url-builder/url-builder';
import { map } from 'rxjs/operators';
import { Observable, combineLatest } from 'rxjs';
import { UserPermissionsStorage } from '../user-permissions/user-permissions-storage';
import { UserPermissions } from '../user-permissions/user-permissions';
import { UserStorage } from '../user/user-storage';

@Component({
    selector: 'admin-dashboard',
    template: require('./admin-dashboard.component.html'),
    styles: [require('./admin-dashboard.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminDashboardComponent implements OnInit {
    public canManageUserAccess$: Observable<boolean>;
    public canManageAuditLogs$: Observable<boolean>;
    public canManageCommonLinks$: Observable<boolean>;
    public canManageAnnouncements$: Observable<boolean>;
    public canManageProtocolActivation$: Observable<boolean>;
    public canReadProtocolActivation$: Observable<boolean>;
    public canMassUploadSiteCommunications$: Observable<boolean>;
    public canEmulate$: Observable<boolean>;
    public canManageCoordinatorEQuery$: Observable<boolean>;
    public canManageAdminEQuery$: Observable<boolean>;
    public canConfigureColumns$: Observable<boolean>;

    public readonly userAccessLink = this.urlBuilder
        .esa()
        .admin()
        .userAccess()
        .url();

    public readonly emulateUserLink = this.urlBuilder
        .esa()
        .globalPopup()
        .emulateUser()
        .url();

    public readonly documentsLink = this.urlBuilder
        .esa()
        .admin()
        .uploadDocuments()
        .url();

    public readonly siteCommunicationsMassUploadLink = this.urlBuilder
        .esa()
        .globalPopup()
        .siteCommunicationsMassUpload()
        .url();

    public readonly announcementsLink = this.urlBuilder
        .esa()
        .admin()
        .announcements()
        .url();

    public readonly commonLink = this.urlBuilder
        .esa()
        .admin()
        .commonLinks()
        .url();

    public readonly protocolActivationLink = this.urlBuilder
        .esa()
        .admin()
        .protocols()
        .url();

    public readonly auditLogsLink = this.urlBuilder
        .esa()
        .admin()
        .auditLogs()
        .url();

    public readonly kitInventoryLink = this.urlBuilder
        .esa()
        .admin()
        .kitInventory()
        .url();

    public readonly coordinatorEQueryLink = this.urlBuilder
        .esa()
        .admin()
        .coordinatorEQuery()
        .url();

    public readonly adminEQueryLink = this.urlBuilder
        .esa()
        .admin()
        .adminEQuery()
        .url();

    public readonly activityReportsLink = this.urlBuilder
        .esa()
        .admin()
        .activityReports()
        .url();


    public readonly editColumnsLink = this.urlBuilder
        .esa()
        .admin()
        .editDefaultColumns()
        .url();

    constructor(
        private readonly permissionsStorage: UserPermissionsStorage,
        private readonly urlBuilder: UrlBuilder,
        private readonly userStorage: UserStorage,
    ) { }

    public ngOnInit() {
        const userPermissions$: Observable<UserPermissions> = this.permissionsStorage.userPermissions;
        const currentUser$ = this.userStorage.currentUser;

        this.canManageUserAccess$ = userPermissions$.pipe(map(
            permissions => permissions.has('AccessUserProfile')
        ));

        this.canManageAuditLogs$ = userPermissions$.pipe(map(
            permissions => permissions.has('ViewAuditLogs')
        ));

        this.canManageCommonLinks$ = userPermissions$.pipe(map(
            permissions => permissions.has('UploadCommonLinks')
        ));

        this.canManageAnnouncements$ = userPermissions$.pipe(map(
            permissions => permissions.has('UploadAnnouncements')
        ));

        this.canManageProtocolActivation$ = userPermissions$.pipe(map(
            permissions => permissions.has('ProtocolAccess')
        ));

        this.canReadProtocolActivation$ = userPermissions$.pipe(map(
            permissions => permissions.has('ProtocolReadAccess')
        ));

        this.canMassUploadSiteCommunications$ = userPermissions$.pipe(map(
            permissions => permissions.has('UploadSiteCommunications')
        ));

        this.canEmulate$ = userPermissions$.pipe(map(
            permissions => permissions.has('EmulateUser')
        ));

        this.canConfigureColumns$ = userPermissions$.pipe(map(
            permissions => permissions.has('ConfigureViews')
        ));

        this.canManageCoordinatorEQuery$ = combineLatest([
            userPermissions$,
            currentUser$,
        ]).pipe(map(
            ([permissions, user]) => user.isInternal && (permissions.has('EQueryCreationRespondClose') || permissions.has('EQueryViewAndRespond')),
        ));

        this.canManageAdminEQuery$ = combineLatest([
            userPermissions$,
            currentUser$,
        ]).pipe(map(
            ([permissions, user]) => user.isAdmin && (permissions.has('EQueryAdministration') || permissions.has('EQueryCreationRespondClose') || permissions.has('EQueryViewAndRespond')),
        ));
    }
}